import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Card,
  TextField,
  Autocomplete,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { AdminModal } from 'views/admin/components/styles';
import api from 'utils/api';
import { DOLPHIN_API } from 'shared/constants/apis';
import PropTypes from 'prop-types';
import logger from 'utils/logger';
import ConfirmContent from './ConfirmContent';

const FORM_IDS = {
  title: 'title',
  s3FilePath: 's3FilePath',
  orgId: 'orgId',
  isLocal: 'isLocal',
  content: 'content',
};

const propTypes = {
  close: PropTypes.func.isRequired,
  reloadTemplates: PropTypes.func.isRequired,
};

const log = logger.createLogger('NewReportModal');

export const NewReportModal = ({ close, reloadTemplates }) => {
  const [formState, setFormState] = useState({
    title: '',
    s3FilePath: '',
    orgIds: '',
    isLocal: false,
    content: '',
  });

  const [incompleteFields, setIncompleteFields] = useState([]);
  const [incompleteWarningModalOpen, setIncompleteWarningModalOpen] =
    useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [orgOptions, setOrgOptions] = useState([]);

  useEffect(() => {
    const getOptions = async () => {
      const organizations = await api.get(DOLPHIN_API.ADMIN_ORGS, {});
      const organizationOptions = organizations.map((org) => {
        return {
          label: org.organizationCode + ' (' + org.name + ')',
          value: {
            organizationId: org.id,
            organizationCode: org.organizationCode,
          },
        };
      });
      setOrgOptions(organizationOptions);
    };
    getOptions();
  }, []);

  const fieldLabels = {
    title: { label: 'Title', required: true, type: 'text' },
    s3FilePath: { label: 'PDF S3 Path', required: true, type: 'text' },
    orgIds: {
      label: 'Organizations with access',
      required: true,
      type: 'select',
      multiselect: true,
    },
    content: {
      label: 'Content',
      required: true,
      type: 'textarea',
    },
  };

  const onValueChange = (key, value) => {
    if (key === FORM_IDS.orgIds) {
      if (value.length > 0) {
        setFormState((prevState) => ({
          ...prevState,
          [key]: value,
          isLocal: false,
        }));
      } else {
        setFormState((prevState) => ({
          ...prevState,
          [key]: value,
          isLocal: true,
        }));
      }
    } else {
      setFormState((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    }
  };

  const sendToServer = async () => {
    setIsLoading(true);
    try {
      const response = await api.post(DOLPHIN_API.ADMIN_REPORT_TEMPLATES, {
        data: {
          organizationIds: formState.orgIds.map(
            (org) => org.value.organizationId
          ),
          title: formState.title,
          s3FilePath: formState.s3FilePath,
          content: JSON.parse(formState.content),
        },
      });
      setResponseData(response);
    } catch (error) {
      log.error('Error creating report template:', error);
      setResponseData({
        message: error.message,
      });
    } finally {
      setIsLoading(false);
      reloadTemplates();
    }
  };

  const handleCloseModal = () => {
    setConfirmModalOpen(false);
    setResponseData(null);
    close();
  };

  const checkIfFormCompleted = () => {
    const _incompleteFields = [];

    const requiredFields = ['title', 'content'];

    for (const key of requiredFields) {
      const value = formState[key];
      if (!value || value.length === 0) {
        _incompleteFields.push(fieldLabels[key].label);
      }
    }
    if (_incompleteFields.length > 0) {
      setIncompleteFields(_incompleteFields);
      return false;
    } else {
      return true;
    }
  };

  const handleSubmitClick = async (event) => {
    event.preventDefault();
    if (!checkIfFormCompleted()) {
      setIncompleteWarningModalOpen(true);
    } else {
      setConfirmModalOpen(true);
    }
  };

  return (
    <>
      <Box m={7}>
        <Stack spacing={3}>
          {Object.keys(fieldLabels).map((key) => {
            switch (fieldLabels[key].type) {
              case 'text':
                return (
                  <TextField
                    key={key}
                    id={key}
                    name={key}
                    fullWidth
                    variant="outlined"
                    label={fieldLabels[key].label}
                    multiline={fieldLabels[key].multiline}
                    onChange={(event) => onValueChange(key, event.target.value)}
                    value={formState[key]}
                  />
                );
              case 'select':
                return (
                  <Autocomplete
                    key={key}
                    id={key}
                    name={key}
                    multiple={fieldLabels[key].multiselect}
                    options={orgOptions}
                    onChange={(_, newValue) => {
                      onValueChange(key, newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={fieldLabels[key].label}
                        fullWidth
                        variant="outlined"
                        color="secondary"
                      />
                    )}
                  />
                );
              case 'textarea':
                return (
                  <div key={key} style={{ margin: '16px 0' }}>
                    <Typography variant="caption" fontWeight="bold">
                      {fieldLabels[key].label}
                    </Typography>
                    <textarea
                      style={{
                        minHeight: '40px',
                        maxHeight: '800px',
                        width: '100%',
                        resize: 'both',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid rgba(0, 0, 0, 0.23)',
                        fontFamily: 'inherit',
                        fontSize: '1rem',
                        overflow: 'auto',
                      }}
                      value={formState[key] || ''}
                      onChange={(e) => onValueChange(key, e.target.value)}
                      rows={Math.min(
                        20,
                        Math.max(
                          4,
                          (formState[key] || '').split('\n').length + 2
                        )
                      )}
                    />
                  </div>
                );
              default:
                return null;
            }
          })}

          <Grid spacing={3} container justifyContent="space-between">
            <Grid>
              <Button
                variant="contained"
                color="primary"
                style={{
                  color: 'white',
                  width: '50%',
                  marginLeft: '25%',
                }}
                onClick={handleSubmitClick}>
                Submit
              </Button>
            </Grid>
            <Grid>
              <Button
                variant="contained"
                color="secondary"
                style={{
                  width: '50%',
                  marginLeft: '25%',
                }}
                onClick={close}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        </Stack>
      </Box>

      {/* Modal for warning */}
      <AdminModal
        open={incompleteWarningModalOpen}
        onClose={() => setIncompleteWarningModalOpen(false)}>
        <Card sx={{ margin: 4, marginTop: 3 }}>
          <Box py={3} px={3} mt={3} mx={3}>
            <Stack spacing={2}>
              <Typography variant="h6" mb={1}>
                Warning
              </Typography>
              {incompleteFields.length > 0 && (
                <>
                  <Typography>
                    The following information is incomplete:
                  </Typography>
                  {incompleteFields.map((field, index) => (
                    <p style={{ color: 'red' }} key={index}>
                      {field}
                    </p>
                  ))}
                </>
              )}
              <Button
                variant="contained"
                color="primary"
                style={{
                  color: '#FFF',
                }}
                onClick={() => setIncompleteWarningModalOpen(false)}>
                Accept
              </Button>
            </Stack>
          </Box>
        </Card>
      </AdminModal>

      {/* Modal for confirm/results */}
      <ConfirmContent
        responseData={responseData}
        confirmModalOpen={confirmModalOpen}
        setConfirmModalOpen={setConfirmModalOpen}
        handleCloseModal={handleCloseModal}
        formState={formState}
        sendToServer={sendToServer}
        setIncompleteWarningModalOpen={setIncompleteWarningModalOpen}
        isLoading={isLoading}
      />
    </>
  );
};

NewReportModal.propTypes = propTypes;

export default NewReportModal;
